import { useCallback, useRef, useState } from "react";
import { Camera, Upload, X, Loader2, Check, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Compose an authenticated image URL — /api/files/{path} accepts ?auth=<token>
// because <img> tags cannot carry Authorization headers.
export const authedFileUrl = (relativeUrl) => {
  if (!relativeUrl) return "";
  const token = localStorage.getItem("fender_token") || "";
  const abs = relativeUrl.startsWith("http") ? relativeUrl : `${BACKEND}${relativeUrl}`;
  const sep = abs.includes("?") ? "&" : "?";
  return `${abs}${sep}auth=${encodeURIComponent(token)}`;
};

/**
 * Reusable photo uploader for Job Cards.
 *
 * Two modes:
 *   • `refId` provided → uploads immediately to /api/uploads with that ref_id.
 *   • `refId` absent  → queues files in memory; caller flushes them after the
 *     parent record is created via `getQueue()` on the exposed ref.
 *
 * Renders quick-shot chips (Front / Rear / Left / Right / Odometer / Fuel /
 * Existing Damage) plus a "+" chip that opens the OS file/camera picker.
 */
export const QUICK_SHOTS = [
  { key: "front",    label: "Front" },
  { key: "rear",     label: "Rear" },
  { key: "left",     label: "Left" },
  { key: "right",    label: "Right" },
  { key: "odometer", label: "Odometer" },
  { key: "fuel",     label: "Fuel Gauge" },
  { key: "damage",   label: "Existing Damage" },
];

export default function PhotoUploader({
  refId,
  context = "job_card",
  photos = [],
  onUploaded,
  onQueueChange,
  queue = [],
  compact = false,
  disabled = false,
  testId = "jc-photo-uploader",
}) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [captionFor, setCaptionFor] = useState(null); // { url, id, caption? }

  const openPicker = (shotKey) => {
    if (disabled) return;
    inputRef.current._shot = shotKey || "";
    inputRef.current.click();
  };

  const handleFiles = useCallback(async (files) => {
    if (!files || files.length === 0) return;
    const shot = inputRef.current._shot || "";
    setBusy(true);
    try {
      for (const file of Array.from(files)) {
        if (!file.type.startsWith("image/")) {
          toast.error(`Skipped ${file.name} — not an image`);
          continue;
        }
        if (refId) {
          // Direct upload
          const fd = new FormData();
          fd.append("file", file);
          const params = { context, ref_id: refId };
          try {
            const r = await api.post("/uploads", fd, {
              params,
              headers: { "Content-Type": "multipart/form-data" },
            });
            const doc = r.data;
            doc.caption = shot ? shot.toUpperCase() : "";
            onUploaded?.(doc);
          } catch (e) {
            toast.error(e.response?.data?.detail || `Upload failed: ${file.name}`);
          }
        } else {
          // Queue locally as data URL for later upload
          const dataUrl = await new Promise((res, rej) => {
            const rd = new FileReader();
            rd.onload = () => res(rd.result);
            rd.onerror = rej;
            rd.readAsDataURL(file);
          });
          const item = {
            _localId: Math.random().toString(36).slice(2),
            file,
            preview: dataUrl,
            caption: shot ? shot.toUpperCase() : "",
            context,
          };
          onQueueChange?.([...queue, item]);
        }
      }
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }, [refId, context, queue, onUploaded, onQueueChange]);

  const removeQueued = (localId) => {
    onQueueChange?.(queue.filter((q) => q._localId !== localId));
  };

  const updateQueuedCaption = (localId, caption) => {
    onQueueChange?.(queue.map((q) => q._localId === localId ? { ...q, caption } : q));
  };

  const removePhoto = async (photo) => {
    // No hard-delete endpoint yet — file removal is P1.  Photos remain in the
    // gallery until a future admin/purge sweep.
    toast.info("Photo delete will land in the next iteration.");
  };

  return (
    <div data-testid={testId}>
      {/* Quick-shot chips */}
      <div className="flex flex-wrap gap-1.5 mb-3">
        {QUICK_SHOTS.map((s) => (
          <button
            type="button"
            key={s.key}
            onClick={() => openPicker(s.key)}
            disabled={disabled || busy}
            data-testid={`jc-photo-shot-${s.key}`}
            className="h-8 px-3 rounded-full border border-[#E4E7EC] text-[11px] font-semibold text-[#457b9d] bg-white hover:bg-[#F1FAEE] hover:border-[#a8dadc] inline-flex items-center gap-1 disabled:opacity-40"
          >
            <Camera size={11} /> {s.label}
          </button>
        ))}
        <button
          type="button"
          onClick={() => openPicker("")}
          disabled={disabled || busy}
          data-testid="jc-photo-add"
          className="h-8 px-3 rounded-full border border-[#e63946]/40 text-[11px] font-semibold text-[#e63946] bg-[#e63946]/8 hover:bg-[#e63946]/15 inline-flex items-center gap-1 disabled:opacity-40"
        >
          {busy ? <Loader2 size={11} className="animate-spin" /> : <Upload size={11} />}
          Add photos
        </button>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        capture="environment"
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
        data-testid="jc-photo-input"
      />

      {/* Uploaded + queued gallery */}
      {(photos.length + queue.length) === 0 ? (
        <div className="rounded-lg border border-dashed border-[#E4E7EC] p-6 text-center text-[12px] text-[#667085]" data-testid="jc-photo-empty">
          <ImageIcon size={22} className="mx-auto text-[#a8dadc] mb-1" />
          Tap a chip above to add photos. On mobile, the camera opens directly.
        </div>
      ) : (
        <div className={`grid gap-2 ${compact ? "grid-cols-3 md:grid-cols-5" : "grid-cols-2 md:grid-cols-4"}`} data-testid="jc-photo-grid">
          {queue.map((q) => (
            <div key={q._localId} className="relative rounded-md overflow-hidden border border-[#F79009]/40 bg-[#FEF0C7]/40" data-testid={`jc-photo-queued-${q._localId}`}>
              <img src={q.preview} alt="" className="w-full h-24 object-cover" />
              <div className="absolute top-1 right-1 flex gap-1">
                <button
                  type="button"
                  onClick={() => removeQueued(q._localId)}
                  className="w-5 h-5 rounded-full bg-white/90 grid place-items-center text-[#e63946] hover:bg-white"
                  aria-label="Remove"
                >
                  <X size={10} />
                </button>
              </div>
              <input
                type="text"
                value={q.caption}
                onChange={(e) => updateQueuedCaption(q._localId, e.target.value)}
                placeholder="Caption"
                className="w-full px-2 py-1 text-[10.5px] bg-white/95 border-t border-[#F79009]/25 focus:outline-none"
              />
              <span className="absolute bottom-8 left-1 text-[9px] font-bold text-[#B54708] bg-white/90 rounded px-1">
                queued
              </span>
            </div>
          ))}
          {photos.map((p) => (
            <div key={p.id} className="relative rounded-md overflow-hidden border border-[#E4E7EC] bg-white group" data-testid={`jc-photo-uploaded-${p.id}`}>
              <img src={authedFileUrl(p.url)} alt={p.caption || ""} className="w-full h-24 object-cover" />
              {refId && (
                <button
                  type="button"
                  onClick={() => removePhoto(p)}
                  className="absolute top-1 right-1 w-5 h-5 rounded-full bg-white/90 grid place-items-center text-[#e63946] hover:bg-white opacity-0 group-hover:opacity-100 transition-opacity"
                  aria-label="Delete"
                >
                  <X size={10} />
                </button>
              )}
              {p.caption && (
                <div className="absolute bottom-0 left-0 right-0 px-1.5 py-0.5 text-[9.5px] font-bold bg-black/60 text-white truncate">
                  {p.caption}
                </div>
              )}
              <span className="absolute top-1 left-1 w-4 h-4 rounded-full bg-[#12B76A] grid place-items-center">
                <Check size={9} className="text-white" strokeWidth={3} />
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Helper for flushing a queued list of PhotoUploader items after a parent
 * job card is created.  Returns the count of successfully-uploaded photos.
 */
export async function flushPhotoQueue(queue, refId, context = "job_card") {
  if (!queue || queue.length === 0) return 0;
  let ok = 0;
  for (const q of queue) {
    try {
      const fd = new FormData();
      fd.append("file", q.file);
      await api.post("/uploads", fd, {
        params: { context: q.context || context, ref_id: refId },
        headers: { "Content-Type": "multipart/form-data" },
      });
      ok++;
    } catch { /* swallow, best-effort */ }
  }
  return ok;
}
